import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@pps/ui/components/ui/dialog';
import { Button } from '@pps/ui/components/ui/button';
import { Input } from '@pps/ui/components/ui/input';
import { Label } from '@pps/ui/components/ui/label';
import { Textarea } from '@pps/ui/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@pps/ui/components/ui/select';
import { Badge } from '@pps/ui/components/ui/badge';
import { ExternalLink, ImagePlus, Loader2, PlayCircle, Trash2, X } from 'lucide-react';
import catalogo from '@/data/training-videos.json';
import { supabase } from '@/integrations/supabase/client';
import {
  ACCEPT_IMAGEM,
  coletarContexto,
  imagemDoArquivo,
  type CapturaTela,
} from '@/lib/screenshot';
import { useAbrirChamado, type SupportPrioridade } from '@/hooks/useSupportTickets';

/**
 * Abertura de chamado de suporte.
 *
 * A CATEGORIA reusa o `moduloId` do acervo de treinamento — a mesma taxonomia
 * que classifica os 44 vídeos. Duas coisas saem de graça daí: a lista de
 * categorias não precisa ser mantida à mão, e escolher a categoria já revela
 * os vídeos daquele módulo, que resolvem boa parte das dúvidas antes do
 * chamado existir.
 *
 * O PRINT chega pronto de fora (capturado no clique do ícone de ajuda, antes
 * de qualquer diálogo cobrir a tela). Aqui ele só é exibido e pode ser
 * removido — quem está reportando precisa poder decidir não mandar a imagem,
 * porque a tela pode ter dado de aluno ou financeiro.
 */

interface VideoItem {
  slug: string;
  arquivo: string;
  titulo: string;
  resumo: string;
  moduloId: number;
  modulo: string;
  duracaoSeg: number;
}

const VIDEOS = catalogo as VideoItem[];

const BUCKET_VIDEOS = 'training-videos';
const VALIDADE_SEG = 60 * 60;

/** Categorias = módulos do acervo, na ordem em que aparecem no manifesto. */
const CATEGORIAS = Array.from(
  new Map(VIDEOS.map((v) => [v.moduloId, v.modulo])).entries(),
).sort((a, b) => a[0] - b[0]);

const PRIORIDADES: SupportPrioridade[] = ['baixa', 'normal', 'alta', 'critica'];

const schema = z.object({
  categoria: z.coerce.number().int(),
  prioridade: z.enum(['baixa', 'normal', 'alta', 'critica']),
  titulo: z.string().trim().min(5).max(160),
  descricao: z.string().trim().min(15).max(4000),
});

type FormValues = z.infer<typeof schema>;

/**
 * Rótulo legível da rota de onde o chamado saiu. A rota crua vai para o banco
 * (o atendente precisa do caminho exato); aqui é só para a pessoa reconhecer a
 * tela — e para "/" não aparecer como uma barra solta no rodapé.
 */
function rotulaRota(rota: string, t: (k: string) => string): string {
  return rota === '/' || rota === '' ? t('support.open.homeScreen') : rota;
}

/**
 * Player do vídeo sugerido, dentro do próprio formulário.
 *
 * A URL é assinada no play, não na montagem da lista: o bucket é privado e a
 * maioria das pessoas não clica em nenhuma das três sugestões — pré-assinar
 * seria três chamadas jogadas fora por abertura de diálogo.
 */
function PreviewVideo({ video }: { video: VideoItem }) {
  const { t } = useTranslation();
  const [url, setUrl] = useState<string | null>(null);
  const [erro, setErro] = useState(false);

  useEffect(() => {
    let cancelado = false;
    setUrl(null);
    setErro(false);
    supabase.storage
      .from(BUCKET_VIDEOS)
      .createSignedUrl(`${video.slug}/${video.arquivo}`, VALIDADE_SEG)
      .then(({ data, error }) => {
        if (cancelado) return;
        if (error || !data) setErro(true);
        else setUrl(data.signedUrl);
      });
    return () => {
      cancelado = true;
    };
  }, [video.slug, video.arquivo]);

  return (
    <div className="mt-2 space-y-1.5 pl-1.5">
      {url ? (
        <video
          key={video.slug}
          src={url}
          controls
          autoPlay
          className="w-full rounded-md border bg-black aspect-video"
        />
      ) : (
        <div className="flex aspect-video w-full items-center justify-center rounded-md border border-dashed text-xs text-muted-foreground">
          {erro ? (
            t('support.open.video.failed')
          ) : (
            <span className="flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              {t('support.open.video.loading')}
            </span>
          )}
        </div>
      )}
      {url && (
        // Escape para quem precisa ler a interface do vídeo de perto: mesmo
        // alargado, o diálogo mostra um 1080p reduzido pela metade.
        <a
          href={url}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
        >
          <ExternalLink className="h-3 w-3" />
          {t('support.open.video.fullSize')}
        </a>
      )}
    </div>
  );
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Print capturado antes de o diálogo abrir. Null quando a captura falhou. */
  captura: CapturaTela | null;
  /** Rota onde o usuário estava — vem de fora porque o diálogo já é overlay. */
  rotaOrigem?: string;
}

export function OpenTicketDialog({ open, onOpenChange, captura, rotaOrigem }: Props) {
  const { t } = useTranslation();
  const location = useLocation();
  const abrir = useAbrirChamado();

  /**
   * A imagem do chamado — uma só. Começa com a captura automática e pode ser
   * removida ou trocada por um arquivo do usuário. Um slot em vez de vários
   * porque `urlDoAnexo` (admin) lê a primeira e única linha: suportar N imagens
   * é mudança dos dois lados, não só deste formulário.
   */
  const [imagem, setImagem] = useState<CapturaTela | null>(captura);
  const [erroImagem, setErroImagem] = useState<string | null>(null);
  const inputArquivo = useRef<HTMLInputElement>(null);
  /** object URLs criados AQUI. O da captura pertence ao HelpButton, que o revoga. */
  const urlsProprias = useRef<string[]>([]);

  const [assistindo, setAssistindo] = useState<VideoItem | null>(null);

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { categoria: 0, prioridade: 'normal', titulo: '', descricao: '' },
  });

  // Reabrir o diálogo com o formulário sujo da vez anterior confunde: o
  // usuário acha que já mandou.
  useEffect(() => {
    if (open) {
      form.reset({ categoria: 0, prioridade: 'normal', titulo: '', descricao: '' });
      setImagem(captura);
      setErroImagem(null);
      setAssistindo(null);
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(
    () => () => {
      urlsProprias.current.forEach((u) => URL.revokeObjectURL(u));
      urlsProprias.current = [];
    },
    [],
  );

  const categoriaSelecionada = form.watch('categoria');

  const sugestoes = useMemo(
    () => VIDEOS.filter((v) => v.moduloId === Number(categoriaSelecionada)).slice(0, 3),
    [categoriaSelecionada],
  );

  // Trocar de assunto troca a lista; manter tocando um vídeo que saiu dela
  // deixa a tela dizendo duas coisas diferentes ao mesmo tempo.
  useEffect(() => {
    setAssistindo(null);
  }, [categoriaSelecionada]);

  async function escolherArquivo(file: File | undefined) {
    if (!file) return;
    const { captura: nova, erro } = await imagemDoArquivo(file);
    if (erro || !nova) {
      setErroImagem(t(`support.open.image.${erro === 'tipo' ? 'badType' : 'tooBig'}`));
      return;
    }
    urlsProprias.current.push(nova.previewUrl);
    setImagem(nova);
    setErroImagem(null);
  }

  async function onSubmit(values: FormValues) {
    try {
      await abrir.mutateAsync({
        ...values,
        rota: rotaOrigem ?? location.pathname,
        contexto: coletarContexto(),
        print: imagem?.blob ?? null,
        printMime: imagem?.mime ?? null,
      });
      toast.success(t('support.open.success'));
      onOpenChange(false);
    } catch (err) {
      toast.error(t('support.open.error'), {
        description: err instanceof Error ? err.message : String(err),
      });
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* Alarga com o player aberto. Os vídeos são gravados em 1920x1080 e são
          screencasts do próprio produto: numa coluna estreita o texto da
          interface vira borrão e o preview não serve para nada. */}
      <DialogContent
        className={`max-h-[90dvh] overflow-y-auto transition-[max-width] duration-200 ${
          assistindo ? 'max-w-4xl' : 'max-w-2xl'
        }`}
      >
        <DialogHeader>
          <DialogTitle>{t('support.open.title')}</DialogTitle>
          <DialogDescription>{t('support.open.subtitle')}</DialogDescription>
        </DialogHeader>

        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label>{t('support.open.category')}</Label>
              <Select
                value={String(form.watch('categoria'))}
                onValueChange={(v) => form.setValue('categoria', Number(v))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORIAS.map(([id, nome]) => (
                    <SelectItem key={id} value={String(id)}>
                      {nome}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label>{t('support.open.priority')}</Label>
              <Select
                value={form.watch('prioridade')}
                onValueChange={(v) => form.setValue('prioridade', v as SupportPrioridade)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PRIORIDADES.map((p) => (
                    <SelectItem key={p} value={p}>
                      {t(`support.priority.${p}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Antes de abrir o chamado, mostrar o que já responde a dúvida.
              Cada item TOCA o vídeo aqui dentro: o ícone de play já prometia
              isso, e mandar o usuário para o acervo faria ele perder o que
              acabou de escrever. */}
          {sugestoes.length > 0 && (
            <div className="rounded-lg border bg-muted/40 p-3 space-y-2">
              <p className="text-xs font-medium text-muted-foreground">
                {t('support.open.maybeThisHelps')}
              </p>
              {sugestoes.map((v) => {
                const aberto = assistindo?.slug === v.slug;
                return (
                  <div key={v.slug}>
                    <button
                      type="button"
                      onClick={() => setAssistindo(aberto ? null : v)}
                      aria-expanded={aberto}
                      className={`flex w-full items-start gap-2 rounded-md p-1.5 text-left text-sm transition-colors hover:bg-background/80 ${
                        aberto ? 'bg-background' : ''
                      }`}
                    >
                      <PlayCircle
                        className={`h-4 w-4 mt-0.5 shrink-0 ${
                          aberto ? 'text-primary' : 'text-primary/70'
                        }`}
                      />
                      <span className="min-w-0 flex-1">
                        <span className="block font-medium truncate">{v.titulo}</span>
                        <span className="block text-xs text-muted-foreground">{v.resumo}</span>
                      </span>
                      {aberto && (
                        <X className="h-4 w-4 mt-0.5 shrink-0 text-muted-foreground" />
                      )}
                    </button>
                    {aberto && <PreviewVideo video={v} />}
                  </div>
                );
              })}
            </div>
          )}

          <div className="space-y-1.5">
            <Label htmlFor="support-titulo">{t('support.open.subject')}</Label>
            <Input
              id="support-titulo"
              placeholder={t('support.open.subjectPlaceholder')}
              {...form.register('titulo')}
            />
            {form.formState.errors.titulo && (
              <p className="text-xs text-destructive">{t('support.open.subjectInvalid')}</p>
            )}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="support-descricao">{t('support.open.details')}</Label>
            <Textarea
              id="support-descricao"
              rows={5}
              placeholder={t('support.open.detailsPlaceholder')}
              {...form.register('descricao')}
            />
            {form.formState.errors.descricao && (
              <p className="text-xs text-destructive">{t('support.open.detailsInvalid')}</p>
            )}
          </div>

          {/* A imagem é opcional e removível: a tela pode conter dado de aluno
              ou financeiro, e a decisão de mandar é de quem está reportando.
              Também é SUBSTITUÍVEL — a captura automática falha (iframe, vídeo,
              imagem de outro domínio) e às vezes o problema nem está na tela em
              que a pessoa estava. Sem o anexo manual, esses casos ficavam sem
              imagem nenhuma. */}
          <div className="space-y-1.5">
            <Label>{t('support.open.screenshot')}</Label>

            <input
              ref={inputArquivo}
              type="file"
              accept={ACCEPT_IMAGEM}
              className="sr-only"
              onChange={(e) => {
                void escolherArquivo(e.target.files?.[0]);
                // Sem isto, escolher o MESMO arquivo depois de remover não
                // dispara change — o valor do input não mudou.
                e.target.value = '';
              }}
            />

            {imagem ? (
              <div className="relative inline-block">
                <img
                  src={imagem.previewUrl}
                  alt={t('support.open.screenshotAlt')}
                  className="max-h-40 rounded-md border"
                />
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  className="absolute top-2 right-2 gap-1.5"
                  onClick={() => setImagem(null)}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                  {t('support.open.removeScreenshot')}
                </Button>
              </div>
            ) : (
              <p className="text-xs text-muted-foreground">
                {captura
                  ? t('support.open.screenshotRemoved')
                  : t('support.open.screenshotFailed')}
              </p>
            )}

            <div className="flex items-center gap-2 pt-1">
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="gap-1.5"
                onClick={() => inputArquivo.current?.click()}
              >
                <ImagePlus className="h-4 w-4" />
                {imagem ? t('support.open.image.replace') : t('support.open.image.attach')}
              </Button>
              <span className="text-xs text-muted-foreground">
                {t('support.open.image.hint')}
              </span>
            </div>

            {erroImagem && <p className="text-xs text-destructive">{erroImagem}</p>}
          </div>

          <DialogFooter className="gap-2">
            {/* Antes mostrava a rota crua, e na home isso virava um "/" solto
                no rodapé — parecia sujeira, não informação. Com rótulo, o campo
                diz para que serve: mostrar de onde o chamado está saindo. */}
            <Badge variant="outline" className="mr-auto self-center text-xs font-normal">
              {t('support.open.openedFrom', { tela: rotulaRota(rotaOrigem ?? location.pathname, t) })}
            </Badge>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={abrir.isPending}>
              {abrir.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {t('support.open.submit')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
